import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import type { ExtendedProfile } from '../../../hooks/profile';

dayjs.extend(utc);
dayjs.extend(timezone);

/** LastModified unix seconds from backend `int(lm.timestamp())`; keep a sane range. */
const MIN_CAPTURE_UNIX = 946684800; // 2000-01-01T00:00:00Z
const MAX_CAPTURE_UNIX = 4102444800; // 2100-01-01T00:00:00Z

export type ParsedUniformImageName =
    | {
          kind: 'structured';
          plate: string;
          driverName: string;
          internalId: string;
          capturedUnix: number;
      }
    | { kind: 'raw'; displayName: string };

/**
 * Parses Metropoline uniform object basename (no extension), shaped like:
 * `{plate}_{driver_name}_{internal_id}_{unix_ts}` — see uniform wellness copy task `dest_key` in `gateway.tasks.customers.uniform_wellness_images`.
 * Underscores in the original driver name are treated as spaces in `driverName`.
 */
export function parseUniformImageBaseName(baseName: string): ParsedUniformImageName {
    const parts = baseName.split('_');
    if (parts.length < 4) {
        return { kind: 'raw', displayName: baseName };
    }
    const tsStr = parts[parts.length - 1];
    if (!/^\d{9,12}$/.test(tsStr)) {
        return { kind: 'raw', displayName: baseName };
    }
    const capturedUnix = parseInt(tsStr, 10);
    if (Number.isNaN(capturedUnix) || capturedUnix < MIN_CAPTURE_UNIX || capturedUnix > MAX_CAPTURE_UNIX) {
        return { kind: 'raw', displayName: baseName };
    }
    const internalId = parts[parts.length - 2];
    const plate = parts[0];
    const driverName = parts.slice(1, -2).join(' ');
    return { kind: 'structured', plate, driverName, internalId, capturedUnix };
}

/** Last path segment of a presigned URL, decoded (uniform object file name). */
export function fileNameFromImageUrl(url: string): string {
    try {
        const u = new URL(url);
        const segment = u.pathname.split('/').filter(Boolean).pop();
        return segment ? decodeURIComponent(segment) : url;
    } catch {
        return url;
    }
}

export function parseUniformImageFileName(fileName: string): ParsedUniformImageName {
    const dot = fileName.lastIndexOf('.');
    const base = dot > 0 ? fileName.slice(0, dot) : fileName;
    return parseUniformImageBaseName(base);
}

export function formatUniformCaptureUnixForDisplay(unixSec: number, profile: ExtendedProfile): string {
    const djs = dayjs.unix(unixSec).utc();
    const tz = profile.timezone?.trim() || dayjs.tz.guess() || 'UTC';
    try {
        return djs.tz(tz).format(profile.dateTimeFormat);
    } catch {
        return djs.utc().format(profile.dateTimeFormat);
    }
}
